/** Formatting in the studio's timezone and currency (never the device's). */
import { localDate } from '@shared/period.ts';
import type { ISOTimeString } from '@astryxdesign/core/TimeInput';
import type { ISODateString } from '@astryxdesign/core/Calendar';

const cache = new Map<string, Intl.DateTimeFormat>();
function dtf(tz: string, opts: Intl.DateTimeFormatOptions) {
  const key = tz + JSON.stringify(opts);
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('ru-RU', { timeZone: tz, ...opts });
    cache.set(key, f);
  }
  return f;
}

export function money(cents: number, currency: string, isFrom = false): string {
  const s = new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
  return isFrom ? `от ${s}` : s;
}

export const time = (iso: string, tz: string) => dtf(tz, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
export const dateTime = (iso: string, tz: string) =>
  dtf(tz, { weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
export const longDateTime = (iso: string, tz: string) =>
  dtf(tz, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** "Пт, 16 окт" for a YYYY-MM-DD calendar date (no timezone shift). */
export function dayLabel(day: string): string {
  return dtf('UTC', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(`${day}T12:00:00Z`));
}
export function dayLong(day: string): string {
  return dtf('UTC', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(`${day}T12:00:00Z`));
}

export function duration(minutes: number): string {
  if (minutes >= 24 * 60) {
    const d = Math.floor(minutes / 1440);
    const h = Math.round((minutes % 1440) / 60);
    const days = d % 10 === 1 && d % 100 !== 11 ? 'день' : [2, 3, 4].includes(d % 10) && ![12, 13, 14].includes(d % 100) ? 'дня' : 'дней';
    return h ? `${d} ${days} ${h} ч` : `${d} ${days}`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} ч` : '', m ? `${m} мин` : ''].filter(Boolean).join(' ');
}

export const today = (tz: string) => localDate(new Date(), tz);

/** Normalizes Russian-style input to E.164 (+7…). Returns null when invalid. */
export function normalizePhone(input: string): string | null {
  const digits = input.replace(/[^\d+]/g, '');
  let d = digits.startsWith('+') ? digits.slice(1) : digits;
  if (d.length === 11 && d.startsWith('8')) d = '7' + d.slice(1);
  if (d.length === 10 && d.startsWith('9')) d = '7' + d;
  return /^[1-9]\d{6,14}$/.test(d) ? `+${d}` : null;
}

export function telHref(e164: string) {
  return `tel:${e164}`;
}

export const WEEKDAY_SHORT = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// Astryx date/time inputs use branded string types; values here are already validated HH:MM / YYYY-MM-DD.
export const isoTime = (s: string | null | undefined) => (s ? (s.slice(0, 5) as ISOTimeString) : undefined);
export const isoDate = (s: string | null | undefined) => (s ? (s as ISODateString) : undefined);
